import { useState } from 'react';
import {
  effectivePaymentSourceOf,
  settlementSourcesOf,
  type CashFlowSource,
  type SourceSettlement,
} from '@progear/finance-core';
import { cn } from '@/lib/utils';
import { formatEur, type CashFlowEntry } from '@/lib/cashFlow';
import { DateText } from '@/components/ui/DateText';
import { SETTLEMENT_SOURCE_META_LIST } from '@/lib/settlementSource';

/**
 * The enriched-detail per-source settlement overview (module 1.3e): every
 * settlement source LHV → SmartAccounts → Erply → Manuaal in priority order with
 * a ✓ when it reported cash, the cash it observed, its newest date, and which one
 * is the TRUTH (highest-priority cash source) vs a superseded observation. The
 * truth + cash amounts come from finance-core (`effectivePaymentSourceOf` /
 * `settlementSourcesOf`); the order has ONE owner (SETTLEMENT_SOURCE_META_LIST,
 * derived from the finance-core priority). The detail's lens-appropriate
 * affordance for the same source data the register column + hover card show (L14).
 *
 * When `onRemoveManual` is provided AND the entry carries a Manuaal fact, the
 * Manuaal row offers "Tühista" — the mark-paid UNDO (a mis-clicked "Märgi
 * laekunuks/makstuks" is retracted). Two-step: the click opens an inline confirm
 * strip naming the amount being removed; only the confirm fires the callback.
 * ONLY the human observation is removable — synced sources are other systems'
 * facts and never offer it. Display-only mounts simply omit the prop.
 */
export function SettlementSources({
  entry,
  onRemoveManual,
  removeManualPending = false,
}: {
  entry: CashFlowEntry;
  /** "Tühista käsitsi makse" — retract the Manuaal settlement events. Offered on
   *  the Manuaal row only when a manual fact exists; the caller runs the mutation. */
  onRemoveManual?: () => void;
  /** The remove mutation is in flight — locks the confirm strip's buttons. */
  removeManualPending?: boolean;
}) {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const truth = effectivePaymentSourceOf(entry);
  const bySource = new Map<CashFlowSource, SourceSettlement>();
  for (const s of settlementSourcesOf(entry)) {
    if (s.paidCents !== 0) bySource.set(s.source, s);
  }
  // The retractable human observation (drives the "Tühista" affordance + confirm
  // strip). After a successful removal the fact disappears from the entry, so the
  // strip auto-dismisses on the refetched data. Callback + fact are collapsed into
  // ONE nullable pair so no condition carries a dead arm (coverage gate).
  const manualFact = bySource.get('manual') ?? null;
  const removal = onRemoveManual && manualFact ? { fire: onRemoveManual, fact: manualFact } : null;
  // Render-time reset: when the manual fact disappears (successful removal → the
  // refetched entry carries none), the confirm flag must clear too — otherwise a
  // LATER re-mark-paid would re-open the strip unbidden on the same mounted detail.
  if (confirmingRemove && !removal) setConfirmingRemove(false);

  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Arvelduse allikad
        </span>
        <span className="text-[9px] text-muted-foreground/60">kõrgeim prioriteet = tõeallikas</span>
      </div>
      <ul className="flex flex-col gap-1">
        {SETTLEMENT_SOURCE_META_LIST.map((m, idx) => {
          const fact = bySource.get(m.source) ?? null;
          const has = fact !== null;
          const isTruth = truth?.source === m.source;
          const superseded = has && !isTruth;
          const prio = idx + 1;
          return (
            <li
              key={m.source}
              className={cn(
                'flex items-center gap-2 rounded border px-2 py-1 text-[11px]',
                isTruth ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/8' : 'border-transparent',
              )}
            >
              <span
                aria-hidden
                className={cn(
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-[9px]',
                  has ? 'bg-[hsl(var(--income))]/20 text-[hsl(var(--income))]' : 'border border-border text-transparent',
                )}
              >
                ✓
              </span>
              <span
                className={cn(
                  'w-9 shrink-0 text-[9px] font-semibold tracking-wide',
                  has ? 'text-foreground/70' : 'text-muted-foreground/40',
                )}
              >
                {m.short}
              </span>
              <span className={cn('flex-1 truncate', has ? 'text-foreground/85' : 'text-muted-foreground/45')}>
                {m.label}
              </span>
              <span
                className={cn(
                  'w-24 shrink-0 text-right tabular-nums',
                  !has && 'text-muted-foreground/40',
                  isTruth && 'font-semibold text-foreground',
                  superseded && 'text-muted-foreground/50 line-through',
                )}
              >
                {fact ? formatEur(fact.paidCents) : '—'}
              </span>
              <span
                className={cn(
                  'w-[4.75rem] shrink-0 text-right text-[10px] tabular-nums',
                  has ? 'text-muted-foreground' : 'text-muted-foreground/40',
                )}
              >
                <DateText iso={fact?.latestPaidOn ?? null} />
              </span>
              {isTruth ? (
                <span className="shrink-0 rounded bg-[hsl(var(--primary))]/15 px-1 text-[8px] font-semibold uppercase tracking-wide text-[hsl(var(--primary))]">
                  Tõeallikas
                </span>
              ) : superseded ? (
                <span className="shrink-0 rounded bg-muted/40 px-1 text-[8px] font-medium uppercase tracking-wide text-muted-foreground">
                  ületatud · P{prio}
                </span>
              ) : (
                <span className="shrink-0 rounded px-1 text-[8px] font-medium uppercase tracking-wide text-muted-foreground/40">
                  P{prio}
                </span>
              )}
              {/* "Tühista" — ONLY on the Manuaal row (the human observation is the
                  only retractable one) and only when a manual fact exists. */}
              {m.source === 'manual' && removal && !confirmingRemove && (
                <button
                  type="button"
                  onClick={() => setConfirmingRemove(true)}
                  className="shrink-0 rounded border border-[hsl(var(--cost))]/40 px-1.5 py-0.5 text-[9px] font-medium text-[hsl(var(--cost))] transition-colors hover:bg-[hsl(var(--cost))]/10"
                >
                  Tühista
                </button>
              )}
            </li>
          );
        })}
      </ul>
      {/* Two-step confirm: name the cash being retracted; only "Eemalda" fires the
          mutation. The synced sources are untouched by design (see the RPC). */}
      {confirmingRemove && removal && (
        <div className="mt-1.5 flex items-center gap-2 rounded border border-[hsl(var(--cost))]/40 bg-[hsl(var(--cost))]/8 px-2 py-1.5 text-[11px]">
          <span className="min-w-0 flex-1 text-foreground/85">
            Eemaldab käsitsi makse märke{' '}
            <span className="font-semibold tabular-nums">{formatEur(removal.fact.paidCents)}</span>
            {' '}— teiste allikate kirjeid ei puudutata.
          </span>
          <button
            type="button"
            disabled={removeManualPending}
            onClick={removal.fire}
            className="shrink-0 rounded border border-[hsl(var(--cost))]/50 px-2 py-0.5 text-[11px] font-medium text-[hsl(var(--cost))] transition-colors hover:bg-[hsl(var(--cost))]/15 disabled:opacity-50"
          >
            {removeManualPending ? 'Eemaldan…' : 'Eemalda'}
          </button>
          <button
            type="button"
            disabled={removeManualPending}
            onClick={() => setConfirmingRemove(false)}
            className="shrink-0 rounded border border-border px-2 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-muted/40 disabled:opacity-50"
          >
            Loobu
          </button>
        </div>
      )}
    </div>
  );
}
